import type { AxiosCollection } from '@/types/Components/Response'
import type { TaxNumber } from '@/types/Models/TaxNumber'
import { notify } from '@kyvg/vue3-notification'
import { defineStore } from 'pinia'
import { none } from '@/services/Helpers'
import TaxNumbersProxy from '@/proxies/Customer/TaxNumbersProxy'

export const useCustomersTaxNumbersStore = defineStore('customers/taxNumbers', {
  state: () => ({
    taxNumbers: [] as TaxNumber[],
    localTaxNumbers: [] as TaxNumber[],
    // uiFlags keyed by item id (real id or temp id)
    uiFlags: {} as Record<string, { _saving?: boolean; _deleting?: boolean; _isNew?: boolean }>,
    selectedTaxNumber: null as TaxNumber | null,
  }),
  getters: {
    selectedTaxNumberValue: (state) => (state.selectedTaxNumber ? JSON.parse(JSON.stringify(state.selectedTaxNumber)) : null),
    taxNumbersWithUi: (state) => state.localTaxNumbers,
    savingMap: (state) => {
      const m: Record<string, boolean> = {}
      Object.keys(state.uiFlags || {}).forEach((k) => {
        m[k] = !!state.uiFlags[k]?._saving
      })
      return m
    },
    deletingMap: (state) => {
      const m: Record<string, boolean> = {}
      Object.keys(state.uiFlags || {}).forEach((k) => {
        m[k] = !!state.uiFlags[k]?._deleting
      })
      return m
    },
    isNewMap: (state) => {
      const m: Record<string, boolean> = {}
      Object.keys(state.uiFlags || {}).forEach((k) => {
        m[k] = !!state.uiFlags[k]?._isNew
      })
      return m
    },
  },
  actions: {
    resetTaxNumbers() {
      this.taxNumbers = []
      this.localTaxNumbers = []
      this.uiFlags = {}
    },
    async store({ companyId, data }: { companyId: string; data: any }) {
      return new TaxNumbersProxy(companyId).create(data)
    },
    fetch(companyId: string) {
      return new TaxNumbersProxy(companyId)
        .all()
        .then((response: AxiosCollection<TaxNumber>) => {
          this.taxNumbers = response.data
          // sync local list as plain TaxNumber objects
          this.localTaxNumbers = this.taxNumbers.map((t) => ({ ...t }))
          return response
        })
        .catch(none)
    },
    reset() {
      this.resetTaxNumbers()
    },
    addLocal() {
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const local: TaxNumber = {
        id: tempId,
        companyId: undefined,
        countryCode: '',
        taxNumber: '',
        createdAt: null,
        updatedAt: null,
      }
      this.localTaxNumbers.unshift(local)
      this.uiFlags[tempId] = { _isNew: true, _saving: false, _deleting: false }
      return local
    },
    removeLocal(id?: string) {
      if (!id) return
      const idx = this.localTaxNumbers.findIndex((x) => x.id === id)
      if (idx >= 0) this.localTaxNumbers.splice(idx, 1)
      delete this.uiFlags[id]
    },
    async createLocal(companyId: string, payload: any, tempId?: string) {
      // mark saving flag
      if (tempId) this.uiFlags[tempId] = { ...(this.uiFlags[tempId] || {}), _saving: true }
      try {
        const res = await new TaxNumbersProxy(companyId).create(payload)
        // backend will create real id; refresh list
        await this.fetch(companyId)
        notify({ group: 'success', text: 'Tax number created' })
        return res
      } catch (e: any) {
        if (tempId) this.uiFlags[tempId] = { ...(this.uiFlags[tempId] || {}), _saving: false }
        notify({ group: 'error', text: e?.message || 'Error creating tax number' })
        throw e
      }
    },
    async updateLocal(companyId: string, id: string, payload: any) {
      // mark saving flag
      this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _saving: true }
      try {
        const res = await new TaxNumbersProxy(companyId).update(id, payload)
        await this.fetch(companyId)
        notify({ group: 'success', text: 'Tax number updated' })
        return res
      } catch (e: any) {
        this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _saving: false }
        notify({ group: 'error', text: e?.message || 'Error updating tax number' })
        throw e
      }
    },
    async destroyLocal(companyId: string, id: string) {
      // if temp id (no real id or starts with tmp), just remove local
      if (!id) return null
      const isTemp = String(id).startsWith('tmp_')
      if (isTemp) {
        this.removeLocal(id)
        notify({ group: 'success', text: 'Tax number removed' })
        return null
      }
      this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _deleting: true }
      try {
        const res = await new TaxNumbersProxy(companyId).destroy(id)
        await this.fetch(companyId)
        notify({ group: 'success', text: 'Tax number deleted' })
        return res
      } catch (e: any) {
        this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _deleting: false }
        notify({ group: 'error', text: e?.message || 'Error deleting tax number' })
        throw e
      }
    },
    // helper unified save: if item has real id -> update, otherwise create
    async saveTaxNumber(companyId: string, item: TaxNumber) {
      const id = item.id as string | undefined
      if (!id || String(id).startsWith('tmp_')) {
        // create: do not send id to backend
        const payload = { countryCode: item.countryCode, taxNumber: item.taxNumber }
        return this.createLocal(companyId, payload, id)
      }
      const payload = { countryCode: item.countryCode, taxNumber: item.taxNumber }
      return this.updateLocal(companyId, id, payload)
    },
    async deleteTaxNumber(companyId: string, itemId?: string) {
      if (!itemId) return this.removeLocal(itemId)
      return this.destroyLocal(companyId, itemId)
    },
    // legacy alias for compatibility
    destroy(companyId: string, id: string) {
      return this.destroyLocal(companyId, id)
    },
    // note: use savingMap/deletingMap/isNewMap getters (storeToRefs) in components
  },
})
